/**
 * The 250px section nav — README §7: sections grouped under 10px/600 `.1em`
 * eyebrows into four clusters (YOU / HOW WE WORK / CONNECTED SYSTEMS / DATA
 * — `buildClusters()`, sections.ts). Presentation only: the button per
 * section is unchanged in identity (same key, same testid, same label, same
 * `onSelect`/`activeKey` contract), so a deep link via `settingsTab` still
 * lands exactly where it always did — only the grouping and the selected
 * item's chrome changed.
 */
import { useStore } from '../../store'
import { buildClusters, clusterOpenDecisionCount } from './sections'

export interface SectionNavProps {
  activeKey: string
  onSelect: (key: string) => void
  /** On a phone this nav IS the screen rather than a column beside a panel. */
  fullWidth?: boolean
}

/**
 * README §7's "Connected systems carry a 7px health dot." The only section
 * in that cluster with a real, already-live boolean behind it is Halo
 * (`store.svc.halo` — the same fact `StatusChip.tsx` reads); `auth`/`entra`/
 * `identity` have no comparable "is it actually connected" signal anywhere
 * in this store, so this deliberately does not invent one for them rather
 * than guess. See task-6-report.md for the same call spelled out.
 */
function HealthDot() {
  const halo = useStore((s) => s.svc.halo)
  return (
    <span
      aria-hidden="true"
      data-testid="settings-nav-halo-health"
      className={['h-[7px] w-[7px] flex-none rounded-full', halo ? 'bg-ok' : 'bg-bad'].join(' ')}
    />
  )
}

export function SectionNav({ activeKey, onSelect, fullWidth = false }: SectionNavProps) {
  const clusters = buildClusters()

  return (
    <nav
      className={[
        'overflow-y-auto bg-sidebar',
        // No padding on a phone: a full-bleed list is the iOS convention, and
        // inset rows waste the width that made the targets small to begin with.
        fullWidth ? 'p-0' : 'flex flex-col gap-4 p-2',
        // README §Layout — 250px on the desktop. On a phone this IS the
        // screen, so a fixed column would leave most of it empty.
        fullWidth ? 'w-full flex-1' : 'w-[250px] flex-none border-r border-subtle',
      ].join(' ')}
      data-testid="settings-nav"
    >
      {clusters.map((cluster) => {
        const openCount = clusterOpenDecisionCount(cluster.key)
        return (
          <div key={cluster.key} className={fullWidth ? '' : 'flex flex-col gap-1'}>
            <div
              data-testid={`settings-cluster-${cluster.key}`}
              className={[
                'flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[.1em] text-lo',
                fullWidth ? 'border-b border-subtle bg-sidebar px-4 py-2' : 'px-3 py-1',
              ].join(' ')}
            >
              <span>{cluster.label}</span>
              {openCount > 0 ? (
                <span
                  data-testid={`settings-cluster-${cluster.key}-badge`}
                  className="inline-flex items-center rounded-full bg-warn-subtle px-1.5 py-0.5 text-[9.5px] font-semibold normal-case tracking-normal text-warn"
                >
                  {openCount} open
                </span>
              ) : null}
            </div>
            {cluster.sections.map((section) => (
              <button
                key={section.key}
                type="button"
                onClick={() => onSelect(section.key)}
                data-testid={`settings-nav-${section.key}`}
                aria-current={section.key === activeKey ? 'true' : undefined}
                className={
                  fullWidth
                    ? [
                        // A THUMB, not a mouse. 52px clears Apple's 44pt
                        // minimum with room either side, so hitting the row
                        // you meant does not need aim — and the rows are
                        // separated rather than floating, which is what
                        // makes an iOS list read as a list.
                        'flex w-full items-center justify-between gap-3 border-b border-subtle px-4 py-[15px] text-left text-[15px] min-h-[52px]',
                        section.key === activeKey ? 'text-cc-blue-text' : 'text-hi',
                      ].join(' ')
                    : [
                        // README §7: "Selected item = --teal-700 fill, white
                        // text, radius 9" — --brand-primary + --text-on-brand
                        // (task 6 binding decisions: this is the pairing that
                        // also works in Legacy, where --brand-primary is the
                        // legacy blue rather than teal).
                        'flex w-full items-center gap-2 rounded-[9px] px-3 py-2 text-left text-[13px]',
                        section.key === activeKey ? 'bg-cc-blue text-on-brand' : 'text-mid hover:bg-card',
                      ].join(' ')
                }
              >
                <span className="min-w-0 flex-1 truncate">{section.label}</span>
                {section.key === 'halo' ? <HealthDot /> : null}
                {/* The disclosure chevron: on a phone this row goes
                    somewhere, and iOS readers expect to be told which rows
                    drill in. */}
                {fullWidth && (
                  <span aria-hidden="true" className="flex-none text-[16px] text-lo">
                    ›
                  </span>
                )}
              </button>
            ))}
          </div>
        )
      })}
    </nav>
  )
}
